// src/pages/commercial/CommercialLaunchpadPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import confetti from 'canvas-confetti';

// --- COMPONENTS: WHITEBOARD ---
function WhiteboardModal({ onClose }) {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        canvas.width = window.innerWidth * 0.8;
        canvas.height = window.innerHeight * 0.8;
        const ctx = canvas.getContext('2d');
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 4;
    }, []);

    const startDraw = (e) => {
        const ctx = canvasRef.current.getContext('2d');
        const x = e.clientX || e.touches[0].clientX;
        const y = e.clientY || e.touches[0].clientY;
        const rect = canvasRef.current.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(x - rect.left, y - rect.top);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const ctx = canvasRef.current.getContext('2d');
        const x = e.clientX || e.touches[0].clientX;
        const y = e.clientY || e.touches[0].clientY;
        const rect = canvasRef.current.getBoundingClientRect();
        ctx.lineTo(x - rect.left, y - rect.top);
        ctx.stroke();
    };

    const stopDraw = () => setIsDrawing(false);

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'grid', placeItems: 'center' }}>
            <div style={{ background: 'white', borderRadius: 20, overflow: 'hidden', position: 'relative' }}>
                <canvas
                    ref={canvasRef}
                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                    style={{ cursor: 'crosshair', touchAction: 'none' }}
                />
                <div style={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 10 }}>
                    <button onClick={clear} style={{ padding: '10px 20px', background: '#cbd5e1', border: 'none', borderRadius: 8, fontWeight: 700 }}>Effacer</button>
                    <button onClick={onClose} style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700 }}>Fermer</button>
                </div>
            </div>
        </div>
    );
}

// --- COMPONENTS: QR MODAL ---
function QRModal({ user, onClose }) {
    // Using a public QR API for demo speed. In prod, use 'qrcode' npm package locally.
    const url = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(window.location.origin + '/booking/' + user.id)}`;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'grid', placeItems: 'center' }} onClick={onClose}>
            <div style={{ background: 'white', padding: 40, borderRadius: 24, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                <h2 style={{ color: 'black', marginTop: 0 }}>Scannez-moi ! 📸</h2>
                <div style={{ background: 'white', padding: 20, borderRadius: 12, border: '1px solid #e2e8f0', display: 'inline-block', marginBottom: 20 }}>
                    <img src={url} alt="QR Code" style={{ display: 'block' }} />
                </div>
                <p style={{ color: '#64748b', margin: 0 }}>Accès direct à mon agenda et mes infos.</p>
                <button onClick={onClose} style={{ marginTop: 20, background: 'black', color: 'white', padding: '10px 30px', borderRadius: 50, border: 'none' }}>Fermer</button>
            </div>
        </div>
    );
}

// --- COMPONENTS: CHECK-IN MODAL ---
// --- COMPONENTS: CHECK-IN MODAL ---
// --- COMPONENTS: CHECK-IN MODAL ---
function CheckInModal({ onCheckIn, onClose }) {
    const [step, setStep] = useState(1); // 1: Form, 2: Loading, 3: Success
    const [data, setData] = useState({
        firstName: '', lastName: '',
        company: '', email: '', phone: '',
        address: '', city: '', zip: '',
        password: '', confirm: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (data.password && data.password !== data.confirm) {
            alert("Les mots de passe ne correspondent pas.");
            return;
        }

        setStep(2); // Loading

        // Simulate API delay
        setTimeout(async () => {
            const fullName = `${data.firstName} ${data.lastName}`.trim();
            // Call parent with full object
            await onCheckIn({ ...data, name: fullName });

            // Trigger Success
            setStep(3);
            // CONFETTI!
            confetti({
                zIndex: 9999,
                particleCount: 200,
                spread: 120,
                origin: { y: 0.6 }
            });
            // No auto-close
        }, 1500);
    };

    const inputStyle = {
        padding: 15, borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.2)',
        background: 'rgba(255,255,255,0.1)',
        color: 'white', fontSize: '1rem',
        outline: 'none', width: '100%',
        textAlign: 'left'
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 100, display: 'grid', placeItems: 'center', overflowY: 'auto', padding: 20 }}>
            <div style={{ width: '100%', maxWidth: 650, padding: 40, textAlign: 'center', color: 'white' }}>

                {step === 1 && (
                    <div className="animate-slide-up">
                        <div style={{ fontSize: '4rem', marginBottom: 20 }}>👋</div>
                        <h1 style={{ fontSize: '2.5rem', marginBottom: 10 }}>Bienvenue chez InfoColab</h1>
                        <p style={{ fontSize: '1.2rem', opacity: 0.7, marginBottom: 40 }}>Créez votre fiche visiteur en quelques secondes.</p>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                                <input required placeholder="Prénom" value={data.firstName} onChange={e => setData({ ...data, firstName: e.target.value })} style={inputStyle} />
                                <input required placeholder="Nom" value={data.lastName} onChange={e => setData({ ...data, lastName: e.target.value })} style={inputStyle} />
                            </div>

                            <input placeholder="Société (Optionnel)" value={data.company} onChange={e => setData({ ...data, company: e.target.value })} style={inputStyle} />

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                                <input required type="email" placeholder="Email" value={data.email} onChange={e => setData({ ...data, email: e.target.value })} style={inputStyle} />
                                <input required type="tel" placeholder="Téléphone" value={data.phone} onChange={e => setData({ ...data, phone: e.target.value })} style={inputStyle} />
                            </div>

                            <input placeholder="Adresse" value={data.address} onChange={e => setData({ ...data, address: e.target.value })} style={inputStyle} />

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                                <input placeholder="Code Postal" value={data.zip} onChange={e => setData({ ...data, zip: e.target.value })} style={inputStyle} />
                                <input placeholder="Ville" value={data.city} onChange={e => setData({ ...data, city: e.target.value })} style={inputStyle} />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                                <input required type="password" placeholder="Mot de passe" value={data.password} onChange={e => setData({ ...data, password: e.target.value })} style={inputStyle} minLength={8} />
                                <input required type="password" placeholder="Confirmer" value={data.confirm} onChange={e => setData({ ...data, confirm: e.target.value })} style={inputStyle} minLength={8} />
                            </div>

                            <button className="animate-pulse" style={{ padding: 20, borderRadius: 50, background: 'white', color: 'black', fontSize: '1.2rem', fontWeight: 700, border: 'none', marginTop: 20, cursor: 'pointer' }}>
                                Je m'enregistre →
                            </button>
                        </form>
                        <button type="button" onClick={onClose} style={{ marginTop: 20, background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>Annuler</button>
                    </div>
                )}

                {step === 2 && (
                    <div className="animate-pulse" style={{ padding: 40 }}>
                        <div style={{ fontSize: '3rem', marginBottom: 20 }}>🔄</div>
                        <h2>Création de votre espace...</h2>
                    </div>
                )}

                {step === 3 && (
                    <div className="animate-scale-in" style={{ background: 'rgba(255,255,255,0.05)', padding: 40, borderRadius: 30, border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ fontSize: '5rem', marginBottom: 10 }}>🎉</div>

                        <h1 style={{ fontSize: '2.5rem', marginBottom: 10, background: 'linear-gradient(to right, #4ade80, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 800 }}>
                            Félicitations, {data.firstName} !
                        </h1>

                        <p style={{ fontSize: '1.2rem', marginBottom: 30, lineHeight: '1.6', opacity: 0.9 }}>
                            Votre <strong>Espace Client Sécurisé</strong> est prêt.<br />
                            Vous pouvez dès maintenant suivre vos projets, valider vos devis et communiquer avec notre équipe.
                        </p>

                        <div style={{ display: 'flex', gap: 30, alignItems: 'center', textAlign: 'left', background: 'white', color: 'black', padding: 20, borderRadius: 20, marginBottom: 30 }}>
                            <div style={{ background: 'white', padding: 10, borderRadius: 10 }}>
                                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://infocolab.com/u/${data.email}`} alt="QR Code" style={{ display: 'block', width: 100, height: 100 }} />
                            </div>
                            <div>
                                <h3 style={{ margin: '0 0 5px 0', fontSize: '1.1rem' }}>Scannez pour accéder</h3>
                                <p style={{ margin: 0, opacity: 0.6, fontSize: '0.9rem' }}>Connectez-vous instantanément<br />à votre tableau de bord.</p>
                                <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                                    <button style={{ padding: '6px 12px', borderRadius: 20, background: 'black', color: 'white', border: 'none', fontSize: '0.8rem', cursor: 'pointer' }}>🔗 Copier le lien</button>
                                    <button style={{ padding: '6px 12px', borderRadius: 20, background: '#f3f4f6', color: 'black', border: 'none', fontSize: '0.8rem', cursor: 'pointer' }}>📩 SMS</button>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 30 }}>
                            <div style={{ background: 'rgba(255,255,255,0.1)', padding: 15, borderRadius: 15 }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: 5 }}>🛡️</div>
                                <div style={{ fontWeight: 'bold' }}>100% Sécurisé</div>
                                <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>Données chiffrées & privées</div>
                            </div>
                            <div style={{ background: 'rgba(255,255,255,0.1)', padding: 15, borderRadius: 15 }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: 5 }}>🚀</div>
                                <div style={{ fontWeight: 'bold' }}>Temps Réel</div>
                                <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>Suivez chaque étape</div>
                            </div>
                        </div>

                        <button onClick={onClose} style={{
                            width: '100%', padding: 18, borderRadius: 50,
                            background: 'white', color: 'black',
                            border: 'none', fontWeight: 800, fontSize: '1.2rem', cursor: 'pointer',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                        }}>
                            Accéder à mon Espace →
                        </button>
                    </div>
                )}
            </div>
            <style>{`
                .animate-slide-up { animation: slideUp 0.5s ease-out; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
                .animate-scale-in { animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                @keyframes scaleIn { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
                .animate-pulse { animation: pulseBtn 2s infinite; }
                @keyframes pulseBtn { 0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); } 100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); } }
            `}</style>
        </div>
    );
}

// --- COMPONENTS: MEMO MODAL (Voice) ---
function MemoModal({ lastProspect, user, onClose }) {
    const [isRecording, setIsRecording] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [status, setStatus] = useState('idle'); // idle, recording, processing, saved

    const recognitionRef = useRef(null);

    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'fr-FR';

            recognitionRef.current.onresult = (event) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                }
                if (finalTranscript) {
                    setTranscript(prev => prev + ' ' + finalTranscript);
                }
            };

            recognitionRef.current.onend = () => {
                setIsRecording(false);
            };
        } else {
            setTranscript("Désolé, votre navigateur ne supporte pas la reconnaissance vocale.");
        }
    }, []);

    const toggleRecording = () => {
        if (isRecording) {
            recognitionRef.current.stop();
            setIsRecording(false);
        } else {
            recognitionRef.current.start();
            setIsRecording(true);
            setStatus('recording');
        }
    };

    const handleSave = async () => {
        setStatus('processing');
        try {
            // Find target (Check-in prospect or just general task)
            let description = `Note Vocale: ${transcript}`;
            let title = 'Note Rapide';

            if (lastProspect) {
                title = `Note sur ${lastProspect.name}`;
            }

            // Create a Task
            await pb.collection('tasks').create({
                title: title,
                description: description,
                status: 'todo',
                assignee: user.id,
                created_by: user.id,
                project: '' // Optional
            });

            setStatus('saved');
            setTimeout(onClose, 2000);
        } catch (err) {
            console.error(err);
            alert("Erreur de sauvegarde: " + err.message);
            setStatus('idle');
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(10px)', zIndex: 100, display: 'grid', placeItems: 'center', color: 'white' }}>
            <div style={{ textAlign: 'center', maxWidth: 500, width: '100%', padding: 40 }}>
                {status === 'saved' ? (
                    <div className="animate-scale-in">
                        <div style={{ fontSize: '5rem', marginBottom: 20 }}>💾</div>
                        <h2 style={{ fontSize: '2rem' }}>Note Sauvegardée !</h2>
                        <p style={{ opacity: 0.7 }}>Retrouvez-la dans vos tâches.</p>
                    </div>
                ) : (
                    <>
                        <h2 style={{ fontSize: '2rem', marginBottom: 40 }}>Quick Mémo 🎙️</h2>

                        <div
                            onClick={toggleRecording}
                            style={{
                                width: 120, height: 120, borderRadius: '50%', background: isRecording ? '#ef4444' : '#334155',
                                margin: '0 auto 40px auto', display: 'grid', placeItems: 'center', fontSize: '3rem', cursor: 'pointer',
                                transition: 'all 0.3s', boxShadow: isRecording ? '0 0 0 20px rgba(239, 68, 68, 0.3)' : 'none',
                                animation: isRecording ? 'pulse-red 1.5s infinite' : 'none'
                            }}
                        >
                            🎙️
                        </div>

                        <div style={{ background: 'rgba(255,255,255,0.1)', padding: 20, borderRadius: 12, minHeight: 150, marginBottom: 40, textAlign: 'left', fontStyle: 'italic', fontSize: '1.2rem' }}>
                            {transcript || "Touchez le micro et parlez..."}
                        </div>

                        <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
                            <button onClick={onClose} style={{ padding: '12px 24px', borderRadius: 50, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer' }}>Annuler</button>
                            {transcript && (
                                <button onClick={handleSave} style={{ padding: '12px 32px', borderRadius: 50, background: 'white', color: 'black', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                                    {status === 'processing' ? 'Sauvegarde...' : 'Enregistrer la note'}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
            <style>{`
                @keyframes pulse-red { 0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); } 70% { box-shadow: 0 0 0 30px rgba(239, 68, 68, 0); } 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); } }
                .animate-scale-in { animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
            `}</style>
        </div>
    );
}

// --- COMPONENTS: CALCULATOR ---
function CalculatorModal({ onClose }) {
    const [display, setDisplay] = useState('0');
    const [equation, setEquation] = useState('');

    const handleNum = (n) => {
        setDisplay(prev => prev === '0' ? String(n) : prev + n);
    };
    const handleOp = (op) => {
        setEquation(display + ' ' + op + ' ');
        setDisplay('0');
    };
    const calc = () => {
        try {
            // eslint-disable-next-line
            const res = eval(equation + display); // Safe enough for internal Calculator
            setDisplay(String(res));
            setEquation('');
        } catch { setDisplay('Err'); }
    };
    const clear = () => { setDisplay('0'); setEquation(''); };

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 260, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Calculatrice</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ background: 'rgba(0,0,0,0.3)', padding: 10, borderRadius: 8, marginBottom: 10, textAlign: 'right', color: 'white', fontFamily: 'monospace', fontSize: 20 }}>
                <div style={{ fontSize: 10, opacity: 0.5, height: 12 }}>{equation}</div>
                {display}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                {['C', '(', ')', '/'].map(b => <button key={b} onClick={() => b === 'C' ? clear() : handleOp(b)} style={{ padding: 10, borderRadius: 6, border: 'none', background: '#334155', color: 'white', cursor: 'pointer' }}>{b}</button>)}
                {['7', '8', '9', '*'].map(b => <button key={b} onClick={() => '0123456789'.includes(b) ? handleNum(b) : handleOp(b)} style={{ padding: 10, borderRadius: 6, border: 'none', background: '0123456789'.includes(b) ? '#475569' : '#334155', color: 'white', cursor: 'pointer' }}>{b}</button>)}
                {['4', '5', '6', '-'].map(b => <button key={b} onClick={() => '0123456789'.includes(b) ? handleNum(b) : handleOp(b)} style={{ padding: 10, borderRadius: 6, border: 'none', background: '0123456789'.includes(b) ? '#475569' : '#334155', color: 'white', cursor: 'pointer' }}>{b}</button>)}
                {['1', '2', '3', '+'].map(b => <button key={b} onClick={() => '0123456789'.includes(b) ? handleNum(b) : handleOp(b)} style={{ padding: 10, borderRadius: 6, border: 'none', background: '0123456789'.includes(b) ? '#475569' : '#334155', color: 'white', cursor: 'pointer' }}>{b}</button>)}
                <button onClick={() => handleNum('0')} style={{ gridColumn: 'span 2', padding: 10, borderRadius: 6, border: 'none', background: '#475569', color: 'white', cursor: 'pointer' }}>0</button>
                <button onClick={() => handleNum('.')} style={{ padding: 10, borderRadius: 6, border: 'none', background: '#475569', color: 'white', cursor: 'pointer' }}>.</button>
                <button onClick={calc} style={{ padding: 10, borderRadius: 6, border: 'none', background: '#3b82f6', color: 'white', cursor: 'pointer' }}>=</button>
            </div>
        </div>
    );
}

// --- COMPONENTS: CHECKLIST ---
function ChecklistModal({ onClose }) {
    const [items, setItems] = useState([
        { id: 1, text: "Batterie PC chargée", done: false },
        { id: 2, text: "Téléphone silencieux", done: false },
        { id: 3, text: "Dossier Client ouvert", done: false },
        { id: 4, text: "Onglets inutiles fermés", done: false },
        { id: 5, text: "Eau à portée de main", done: false },
    ]);
    const toggle = (id) => setItems(items.map(i => i.id === id ? { ...i, done: !i.done } : i));

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 260, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Checklist Avant-Match</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(i => (
                    <div key={i.id} onClick={() => toggle(i.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', opacity: i.done ? 0.5 : 1 }}>
                        <div style={{ width: 18, height: 18, borderRadius: 4, border: '1px solid #94a3b8', background: i.done ? '#22c55e' : 'transparent', display: 'grid', placeItems: 'center', fontSize: 10 }}>{i.done && '✓'}</div>
                        <span style={{ color: 'inherit', textDecoration: i.done ? 'line-through' : 'none', fontSize: '0.9rem' }}>{i.text}</span>
                    </div>
                ))}
            </div>
            <button onClick={() => setItems(items.map(i => ({ ...i, done: false })))} style={{ width: '100%', marginTop: 12, padding: 8, background: 'var(--theme-btn-border)', border: 'none', color: 'inherit', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}>Tout reset</button>
        </div>
    );
}

// --- COMPONENTS: DECISION (DICE) ---
function DecisionModal({ onClose }) {
    const [result, setResult] = useState(null);
    const [animating, setAnimating] = useState(false);

    const roll = () => {
        setAnimating(true);
        setResult(null);
        setTimeout(() => {
            setAnimating(false);
            setResult(Math.floor(Math.random() * 6) + 1);
        }, 1000);
    };

    const flip = () => {
        setAnimating(true);
        setResult(null);
        setTimeout(() => {
            setAnimating(false);
            setResult(Math.random() > 0.5 ? 'Pile 🪙' : 'Face 👑');
        }, 1000);
    };

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 200, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Décision</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ height: 80, display: 'grid', placeItems: 'center', fontSize: '2.5rem', marginBottom: 10 }}>
                {animating ? <span className="animate-spin">🎲</span> : (result || '❓')}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button onClick={roll} style={{ padding: 8, background: '#3b82f6', border: 'none', color: 'white', borderRadius: 6, cursor: 'pointer' }}>Dé</button>
                <button onClick={flip} style={{ padding: 8, background: '#eab308', border: 'none', color: 'white', borderRadius: 6, cursor: 'pointer' }}>Pièce</button>
            </div>
        </div>
    );
}

// --- COMPONENTS: TIMER ---
function TimerModal({ onClose }) {
    const [seconds, setSeconds] = useState(0);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        let int;
        if (running && seconds > 0) {
            int = setInterval(() => setSeconds(s => s - 1), 1000);
        } else if (seconds === 0) {
            setRunning(false);
        }
        return () => clearInterval(int);
    }, [running, seconds]);

    const setTime = (m) => {
        setSeconds(m * 60);
        setRunning(true);
    };

    const fmt = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 220, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Chrono Pitch</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ fontSize: '3rem', fontWeight: 700, fontFamily: 'monospace', color: seconds < 10 && seconds > 0 ? '#ef4444' : 'inherit', marginBottom: 15 }}>
                {fmt(seconds)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5, marginBottom: 10 }}>
                {[1, 3, 5].map(m => (
                    <button key={m} onClick={() => setTime(m)} style={{ padding: '6px 0', background: '#334155', border: 'none', color: 'white', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}>{m}m</button>
                ))}
            </div>
            <button onClick={() => setRunning(!running)} style={{ width: '100%', padding: 8, background: running ? '#ef4444' : '#22c55e', border: 'none', color: 'white', borderRadius: 6, cursor: 'pointer' }}>
                {running ? 'Pause' : 'Start'}
            </button>
        </div>
    );
}

// --- COMPONENTS: POST-IT ---
function PostItModal({ onClose }) {
    const [text, setText] = useState(() => localStorage.getItem('quick_postit') || '');

    useEffect(() => {
        localStorage.setItem('quick_postit', text);
    }, [text]);

    return (
        <div style={{ position: 'fixed', top: 100, right: 40, width: 250, height: 250, background: '#fef3c7', borderRadius: 2, padding: 0, boxShadow: '5px 5px 15px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)', zIndex: 50, transform: 'rotate(-1deg)' }}>
            <div style={{ height: 30, background: 'rgba(0,0,0,0.05)', padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'move' }}>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#78350f', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
            </div>
            <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Note rapide..."
                style={{ width: '100%', height: 'calc(100% - 30px)', background: 'transparent', border: 'none', outline: 'none', padding: 16, fontSize: '1.2rem', fontFamily: '"Comic Sans MS", "Marker Felt", sans-serif', color: '#451a03', resize: 'none' }}
            />
        </div>
    );
}

// --- COMPONENTS: FOCUS OVERLAY ---
function FocusOverlay({ onClose }) {
    const [pos, setPos] = useState({ x: -100, y: -100 });

    useEffect(() => {
        const move = (e) => setPos({ x: e.clientX, y: e.clientY });
        window.addEventListener('mousemove', move);
        const esc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', esc);
        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('keydown', esc);
        };
    }, [onClose]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999, cursor: 'none',
                background: `radial-gradient(circle 150px at ${pos.x}px ${pos.y}px, transparent 0%, rgba(0,0,0,0.85) 100%)`
            }}
        >
            <div style={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', color: 'white', opacity: 0.5, pointerEvents: 'none' }}>
                Mode Focus • Appuyez pour quitter
            </div>
        </div>
    );
}

// --- COMPONENTS: OBJECTION KILLER ---
function ObjectionModal({ onClose }) {
    const objections = {
        "Prix": ["C'est un investissement, pas une dépense.", "Comparé au coût de ne rien faire ?", "On peut ajuster le périmètre."],
        "Temps": ["Justement, cet outil vous en fait gagner.", "On s'occupe de toute la migration.", "Le setup ne prend que 2h."],
        "Concurrence": ["Ils sont bien, mais nous avons [X].", "Préférez-vous le low-cost ou le premium ?", "Notre support est 100% français."],
        "Décideur": ["Qui d'autre doit valider ce projet ?", "Voulez-vous que je leur présente ?", "Quels sont leurs critères ?"]
    };
    const [category, setCategory] = useState('Prix');

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 300, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Objection Killer</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 5, marginBottom: 15, overflowX: 'auto', paddingBottom: 5 }}>
                {Object.keys(objections).map(k => (
                    <button key={k} onClick={() => setCategory(k)} style={{ padding: '4px 8px', borderRadius: 4, border: 'none', background: category === k ? '#ef4444' : '#334155', color: 'white', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{k}</button>
                ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {objections[category].map((arg, i) => (
                    <div key={i} style={{ padding: 10, background: 'var(--theme-btn-border)', borderRadius: 6, color: 'inherit', fontSize: '0.85rem', borderLeft: '3px solid #ef4444' }}>{arg}</div>
                ))}
            </div>
        </div>
    );
}

// --- COMPONENTS: ICE BREAKERS ---
function IceBreakerModal({ onClose }) {
    const phrases = [
        "Comment s'est passée votre semaine ?",
        "J'ai vu que votre entreprise a...",
        "Quels sont vos gros enjeux du trimestre ?",
        "Prêt pour le weekend ?",
        "Vous êtes basé où exactement ?"
    ];
    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 280, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Ice Breakers</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {phrases.map((p, i) => (
                    <div key={i} onClick={() => navigator.clipboard.writeText(p)} style={{ padding: 10, background: 'var(--theme-btn-border)', borderRadius: 6, color: 'inherit', fontSize: '0.85rem', cursor: 'pointer', transition: 'background 0.2s' }} className="hover:bg-gray-700">
                        {p}
                    </div>
                ))}
                <div style={{ textAlign: 'center', fontSize: 10, opacity: 0.5, marginTop: 5 }}>Cliquer pour copier</div>
            </div>
        </div>
    );
}

// --- COMPONENTS: CONVERTER ---
function ConverterModal({ onClose }) {
    const [val, setVal] = useState('');
    const [res, setRes] = useState('');

    // Simple Tax calc for now
    useEffect(() => {
        const num = parseFloat(val);
        if (!isNaN(num)) setRes((num * 1.2).toFixed(2));
        else setRes('');
    }, [val]);

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 200, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>HT → TTC (20%)</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <input
                type="number"
                value={val}
                onChange={e => setVal(e.target.value)}
                placeholder="Montant HT"
                style={{ width: '100%', padding: 8, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: 'white', marginBottom: 10 }}
            />
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#22c55e', textAlign: 'center' }}>
                {res ? `${res} €` : '-'}
            </div>
        </div>
    );
}

// --- COMPONENTS: NEXT SLOT ---
function NextSlotModal({ onClose }) {
    // Mock slots for demo
    const slots = ["Demain 14h00", "Jeudi 10h00", "Vendredi 11h30"];

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 200, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Prochains Créneaux</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {slots.map((s, i) => (
                    <div key={i} style={{ padding: 10, background: 'var(--theme-btn-border)', borderRadius: 6, color: 'inherit', fontSize: '0.9rem' }}>
                        {s}
                    </div>
                ))}
            </div>
        </div>
    );
}

// --- COMPONENTS: ZEN MODE ---
function ZenModal({ onClose }) {
    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(10px)' }}>
            <button onClick={onClose} style={{ position: 'absolute', top: 20, right: 30, background: 'white', border: 'none', padding: '10px 20px', borderRadius: 50, cursor: 'pointer', fontWeight: 'bold' }}>Quitter</button>
            <div style={{ width: 300, height: 300, background: '#a7f3d0', borderRadius: '50%', animation: 'breathe 8s infinite ease-in-out', display: 'grid', placeItems: 'center', boxShadow: '0 0 100px #a7f3d0' }}>
                <span style={{ fontSize: '2rem', zIndex: 10 }}>Inspirer...</span>
            </div>
            <style>{`@keyframes breathe { 0%, 100% { transform: scale(0.5); opacity: 0.5; content: 'Inspirer'; } 50% { transform: scale(1.5); opacity: 1; content: 'Expirer'; } }`}</style>
        </div>
    );
}

// --- COMPONENTS: WEATHER ---
function WeatherModal({ onClose }) {
    const [city, setCity] = useState('Paris');
    const [weather, setWeather] = useState({ temp: 18, cond: '☀️' });

    // Mock weather
    const check = () => {
        const conds = ['☀️', '☁️', '🌧️', '⛈️', '❄️'];
        setWeather({
            temp: Math.floor(Math.random() * 30),
            cond: conds[Math.floor(Math.random() * conds.length)]
        });
    };

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 220, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Météo Client</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                <input value={city} onChange={e => setCity(e.target.value)} style={{ flex: 1, padding: 6, borderRadius: 6, border: 'none', background: 'var(--theme-btn-border)', color: 'inherit', fontSize: '0.9rem' }} />
                <button onClick={check} style={{ padding: '0 10px', borderRadius: 6, border: 'none', background: '#3b82f6', color: 'white', cursor: 'pointer' }}>🔎</button>
            </div>
            <div style={{ textAlign: 'center', color: 'inherit' }}>
                <div style={{ fontSize: '3rem' }}>{weather.cond}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{weather.temp}°C</div>
            </div>
        </div>
    );
}

// --- COMPONENTS: WORLD CLOCK ---
function WorldClockModal({ onClose }) {
    const [time, setTime] = useState(new Date());

    useEffect(() => {
        const t = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const getT = (zone) => time.toLocaleTimeString('fr-FR', { timeZone: zone, hour: '2-digit', minute: '2-digit' });

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 240, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Horloge Monde</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[['Paris', 'Europe/Paris'], ['New York', 'America/New_York'], ['Tokyo', 'Asia/Tokyo']].map(([label, zone]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', color: 'inherit', fontSize: '0.9rem' }}>
                        <span style={{ opacity: 0.7 }}>{label}</span>
                        <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{getT(zone)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// --- COMPONENTS: DISCOUNT CALCULATOR ---
function DiscountModal({ onClose }) {
    const [price, setPrice] = useState('');
    const [budget, setBudget] = useState('');
    const [res, setRes] = useState(null);

    useEffect(() => {
        const p = parseFloat(price);
        const b = parseFloat(budget);
        if (p && b) {
            const pct = ((p - b) / p) * 100;
            setRes(pct.toFixed(1));
        } else setRes(null);
    }, [price, budget]);

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 220, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Calc. Remise</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                <input type="number" placeholder="Prix initial" value={price} onChange={e => setPrice(e.target.value)} style={{ padding: 8, background: 'var(--theme-btn-border)', border: '1px solid var(--theme-modal-border)', borderRadius: 6, color: 'inherit', fontSize: '0.9rem' }} />
                <input type="number" placeholder="Budget client" value={budget} onChange={e => setBudget(e.target.value)} style={{ padding: 8, background: 'var(--theme-btn-border)', border: '1px solid var(--theme-modal-border)', borderRadius: 6, color: 'inherit', fontSize: '0.9rem' }} />
            </div>
            {res && (
                <div style={{ textAlign: 'center', color: parseFloat(res) > 20 ? '#ef4444' : '#22c55e', fontWeight: 'bold' }}>
                    Remise : -{res}%
                </div>
            )}
        </div>
    );
}

// --- COMPONENTS: PASSWORD GEN ---
function PasswordModal({ onClose }) {
    const [pwd, setPwd] = useState('');

    const gen = () => {
        const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
        let p = "";
        for (let i = 0; i < 12; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
        setPwd(p);
    };

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 240, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Générateur MDP</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div onClick={() => navigator.clipboard.writeText(pwd)} style={{ padding: 10, background: 'rgba(0,0,0,0.3)', borderRadius: 6, color: '#22c55e', fontFamily: 'monospace', textAlign: 'center', marginBottom: 10, cursor: 'pointer', minHeight: 40, display: 'grid', placeItems: 'center' }}>
                {pwd || '...'}
            </div>
            <button onClick={gen} style={{ width: '100%', padding: 8, background: '#3b82f6', border: 'none', color: 'white', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem' }}>Générer</button>
        </div>
    );
}

// --- COMPONENTS: SOUNDBOARD (Replaces Gong) ---
function SoundboardModal({ onClose }) {
    const sounds = [
        { id: 'clap', emoji: '👏', label: 'Bravo' },
        { id: 'laugh', emoji: '😂', label: 'Rire' },
        { id: 'cricket', emoji: '🦗', label: 'Malaise' },
        { id: 'gong', emoji: '🏆', label: 'Vente !' },
    ];
    // Mock sound play - in real app would use Audio()
    const play = (id) => {
        const btn = document.getElementById(`sound-${id}`);
        if (btn) {
            btn.style.transform = 'scale(0.9)';
            setTimeout(() => btn.style.transform = 'scale(1)', 100);
        }
    };

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 260, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Boîte à Bruits</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {sounds.map(s => (
                    <button id={`sound-${s.id}`} key={s.id} onClick={() => play(s.id)} style={{ padding: 15, background: '#334155', border: 'none', borderRadius: 8, cursor: 'pointer', transition: 'all 0.1s' }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: 5 }}>{s.emoji}</div>
                        <div style={{ fontSize: '0.8rem', color: 'white', opacity: 0.8 }}>{s.label}</div>
                    </button>
                ))}
            </div>
        </div>
    );
}

// --- COMPONENTS: QUOTE ---
function QuoteModal({ onClose }) {
    const quotes = [
        "Le succès c'est d'aller d'échec en échec sans perdre son enthousiasme. - Churchill",
        "Ne vendez pas, aidez à acheter.",
        "La meilleure façon de prédire l'avenir est de le créer. - Drucker",
        "Votre attitude détermine votre altitude. - Ziglar",
        "Qui ose gagne."
    ];
    const [q, setQ] = useState(quotes[0]);

    useEffect(() => setQ(quotes[Math.floor(Math.random() * quotes.length)]), []);

    return (
        <div style={{ position: 'fixed', bottom: 20, left: 340, width: 260, background: 'var(--theme-modal-bg)', borderRadius: 12, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 50, border: '1px solid var(--theme-modal-border)', color: 'var(--theme-modal-text)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, color: 'inherit' }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Citation du Jour</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ color: 'inherit', fontStyle: 'italic', fontSize: '0.9rem', lineHeight: '1.4', marginBottom: 15, borderLeft: '3px solid #f59e0b', paddingLeft: 10 }}>
                "{q}"
            </div>
            <button onClick={() => setQ(quotes[Math.floor(Math.random() * quotes.length)])} style={{ width: '100%', padding: 8, background: 'var(--theme-btn-border)', border: 'none', color: 'inherit', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}>Autre citation</button>
        </div>
    );
}


// --- COMPONENTS: SCREENSAVER ---
function ScreenSaver({ images, onClose }) {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setIdx(i => (i + 1) % images.length), 4000);
        return () => clearInterval(t);
    }, [images]);

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'black', zIndex: 200, cursor: 'none' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${images[idx]})`, backgroundSize: 'cover', backgroundPosition: 'center', transition: 'background-image 1s ease-in-out' }} />
            <div style={{ position: 'absolute', bottom: 50, left: 0, right: 0, textAlign: 'center', color: 'white', textShadow: '0 2px 10px rgba(0,0,0,0.5)', fontSize: '2rem', animation: 'pulse 2s infinite' }}>
                Touchez l'écran pour commencer
            </div>
            <style>{`@keyframes pulse { 0% { opacity: 0.5; } 50% { opacity: 1; } 100% { opacity: 0.5; } }`}</style>
        </div>
    );
}

// --- COMPONENTS: PORTFOLIO MODAL (Slider, Database Linked) ---
function PortfolioModal({ onClose, theme }) {
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        pb.collection('site_realisations').getFullList({ sort: 'sort_order, -created', filter: 'is_published = true' })
            .then(res => {
                setItems(res?.length ? res : []);
                setLoading(false);
            })
            .catch(err => {
                console.error("Portfolio Error", err);
                setItems([]);
                setLoading(false);
            });
    }, []);

    const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % items.length); };
    const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + items.length) % items.length); };

    const isLight = theme === 'light';

    // Fallback if empty
    const displayItems = items.length > 0 ? items : [
        { id: 1, title: 'Aucune réalisation', excerpt: 'Ajoutez des projets dans la base de données.', cover_image: '' }
    ];

    const current = displayItems[idx];
    const imageUrl = current.cover_image
        ? pb.files.getUrl(current, current.cover_image)
        : 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80';

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(10px)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>

            <button onClick={onClose} style={{ position: 'absolute', top: 30, right: 30, background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: 50, height: 50, borderRadius: '50%', fontSize: '1.5rem', cursor: 'pointer', zIndex: 310 }}>&times;</button>

            {loading ? <div>Chargement...</div> : (
                <div style={{ width: '90%', height: '80%', position: 'relative', display: 'flex', borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', background: '#0f172a' }} onClick={e => e.stopPropagation()}>

                    {/* Image Limit */}
                    <div style={{ flex: 2, position: 'relative', overflow: 'hidden' }}>
                        <div style={{
                            position: 'absolute', inset: 0,
                            backgroundImage: `url(${imageUrl})`,
                            backgroundSize: 'cover', backgroundPosition: 'center',
                            transition: 'background-image 0.5s ease-in-out'
                        }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.4), transparent)' }} />

                        {/* Navigation Arrows on Image */}
                        <button onClick={prev} style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: 'none', color: 'white', width: 50, height: 50, borderRadius: '50%', fontSize: '1.5rem', cursor: 'pointer' }}>‹</button>
                    </div>

                    {/* Content Panel */}
                    <div style={{ flex: 1, padding: 60, display: 'flex', flexDirection: 'column', justifyContent: 'center', background: isLight ? 'white' : '#1e293b', color: isLight ? '#0f172a' : 'white' }}>
                        <div style={{ opacity: 0.5, textTransform: 'uppercase', letterSpacing: 2, fontSize: '0.9rem', marginBottom: 10 }}>Portfolio • {idx + 1}/{displayItems.length}</div>
                        <h2 style={{ fontSize: '2.5rem', fontWeight: 800, margin: '0 0 20px 0', lineHeight: 1.1 }}>{current.title}</h2>
                        <p style={{ opacity: 0.7, lineHeight: 1.6, fontSize: '1.1rem', marginBottom: 40 }}>
                            {current.excerpt || "Aucune description disponible pour ce projet."}
                        </p>

                        <div style={{ display: 'flex', gap: 20, marginTop: 'auto' }}>
                            <button onClick={prev} style={{ flex: 1, padding: '15px 0', borderRadius: 10, background: 'transparent', border: `1px solid ${isLight ? '#334155' : 'rgba(255,255,255,0.3)'}`, color: 'inherit', cursor: 'pointer', fontWeight: 600 }}>Précédent</button>
                            <button onClick={next} style={{ flex: 1, padding: '15px 0', borderRadius: 10, background: '#3b82f6', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 600, boxShadow: '0 10px 20px rgba(59, 130, 246, 0.3)' }}>Suivant</button>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
}

// --- COMPONENTS: AGENDA MODAL (Native System - UNUSED) ---
function AgendaModal({ user, onClose, theme }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [creatingSlot, setCreatingSlot] = useState(null); // { date, hour }
    const [selectedMeeting, setSelectedMeeting] = useState(null); // For details
    const [hoveredSlot, setHoveredSlot] = useState(null); // { dayIndex, hour }
    const [newTitle, setNewTitle] = useState('');

    // Calculate week start (Monday)
    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    };

    const weekStart = getWeekStart(currentDate);
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    const hours = Array.from({ length: 10 }, (_, i) => i + 9); // 9h to 18h

    useEffect(() => {
        loadMeetings();
    }, [weekStart]);

    const loadMeetings = async () => {
        setLoading(true);
        try {
            // Fetch meetings for this commercial only
            const res = await pb.collection('meetings').getFullList({
                sort: '-start',
                filter: `commercial_id = "${user.id}"`
            });
            setMeetings(res);
        } catch (e) {
            console.error("Error loading meetings", e);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSlot = (dayIndex, hour) => {
        const date = new Date(weekStart);
        date.setDate(date.getDate() + dayIndex);
        date.setHours(hour, 0, 0, 0);
        setCreatingSlot({ date, hour });
        setNewTitle(''); // Reset
    };

    const confirmCreateSlot = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!newTitle) return;
        if (!user?.id) return window.alert("Erreur: Utilisateur non identifié.");

        setSubmitting(true);
        try {
            const startStr = creatingSlot.date.toISOString();
            const endStr = new Date(creatingSlot.date.getTime() + 60 * 60 * 1000).toISOString();

            await pb.collection('meetings').create({
                title: newTitle,
                start: startStr,
                end: endStr,
                commercial_id: user.id
            });

            setCreatingSlot(null); // Close modal IMMEDIATELY
            loadMeetings(); // Background reload
        } catch (err) {
            console.error(err);
            window.alert("Erreur lors de la création : " + (err.message || err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm("Supprimer ce RDV ?")) return;
        try {
            await pb.collection('meetings').delete(id);
            loadMeetings();
        } catch (err) { alert(err.message); }
    };

    const changeWeek = (delta) => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + (delta * 7));
        setCurrentDate(d);
    };

    const isLight = theme === 'light';
    const styles = {
        bg: isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.9)',
        text: isLight ? '#1e293b' : 'white',
        border: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        cardBg: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
        cardHeader: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
        today: isLight ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
        btnClose: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
        btnHover: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)',
        input: isLight ? { background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#0f172a' } : { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: styles.bg, backdropFilter: 'blur(20px)', zIndex: 300, display: 'flex', flexDirection: 'column', padding: 40, color: styles.text }}>

            {/* NEW MEETING MODAL */}
            {creatingSlot && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 310, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.5)' }} onClick={() => setCreatingSlot(null)}>
                    <div onClick={e => e.stopPropagation()} className="animate-scale-in" style={{ width: 400, background: isLight ? 'white' : '#1e293b', padding: 30, borderRadius: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', color: isLight ? '#0f172a' : 'white' }}>
                        <h3 style={{ marginTop: 0, marginBottom: 20 }}>Nouveau Rendez-vous</h3>
                        <div style={{ marginBottom: 20, opacity: 0.7 }}>
                            {creatingSlot.date.toLocaleDateString()} à {creatingSlot.hour}h00
                        </div>
                        <form onSubmit={confirmCreateSlot}>
                            <input
                                autoFocus
                                placeholder="Titre (ex: Point Projet, Démo...)"
                                value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                                style={{ width: '100%', padding: 15, borderRadius: 10, outline: 'none', marginBottom: 20, ...styles.input }}
                            />
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <button type="button" onClick={() => setCreatingSlot(null)} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer' }}>Annuler</button>
                                <button type="submit" disabled={!newTitle || submitting} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 600, cursor: 'pointer' }}>{submitting ? 'Enregistrement...' : 'Valider'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MEETING DETAILS MODAL */}
            {selectedMeeting && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 320, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedMeeting(null)}>
                    <div onClick={e => e.stopPropagation()} className="animate-scale-in" style={{ width: 400, background: isLight ? 'white' : '#1e293b', padding: 30, borderRadius: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', color: isLight ? '#0f172a' : 'white', position: 'relative' }}>
                        <button onClick={() => setSelectedMeeting(null)} style={{ position: 'absolute', top: 15, right: 15, background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'inherit', opacity: 0.5 }}>&times;</button>

                        <h3 style={{ marginTop: 0, marginBottom: 5, fontSize: '1.5rem' }}>{selectedMeeting.title}</h3>
                        <div style={{ opacity: 0.7, marginBottom: 20, fontSize: '0.9rem' }}>
                            📅 {new Date(selectedMeeting.start).toLocaleDateString()} <br />
                            ⏰ {new Date(selectedMeeting.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(selectedMeeting.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>

                        <div style={{ padding: 15, background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.05)', borderRadius: 10, marginBottom: 20 }}>
                            <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>Invité par : Vous</div>
                            {/* Future: Add notes, client name, etc here */}
                        </div>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={(e) => { setSelectedMeeting(null); handleDelete(selectedMeeting.id, e); }} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: '#ef4444', color: 'white', fontWeight: 600, cursor: 'pointer' }}>Supprimer</button>
                            <button onClick={() => setSelectedMeeting(null)} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)', color: 'inherit', fontWeight: 600, cursor: 'pointer' }}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
                <div>
                    <h2 style={{ fontSize: '2rem', margin: 0, fontWeight: 700 }}>📅 Agenda Commercial</h2>
                    <p style={{ opacity: 0.6, margin: 0 }}>Gérez vos disponibilités en temps réel</p>
                </div>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                    <div style={{ display: 'flex', background: styles.cardBg, borderRadius: 10, padding: 5 }}>
                        <button onClick={() => changeWeek(-1)} style={{ padding: '8px 15px', background: 'none', border: 'none', color: styles.text, cursor: 'pointer' }}>←</button>
                        <span style={{ padding: '8px 15px', fontWeight: 600 }}>Semaine du {weekStart.toLocaleDateString()}</span>
                        <button onClick={() => changeWeek(1)} style={{ padding: '8px 15px', background: 'none', border: 'none', color: styles.text, cursor: 'pointer' }}>→</button>
                    </div>
                    <button onClick={onClose} style={{ width: 40, height: 40, borderRadius: '50%', background: styles.btnClose, border: 'none', color: styles.text, fontSize: '1.2rem', cursor: 'pointer' }}>&times;</button>
                </div>
            </div>

            {/* Grid */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', gap: 1, background: styles.border, border: `1px solid ${styles.border}`, borderRadius: 16, overflow: 'hidden' }}>

                {/* Header Row (Days) */}
                <div style={{ background: styles.cardHeader }}></div> {/* Time Corner */}
                {days.map((day, i) => {
                    const d = new Date(weekStart);
                    d.setDate(d.getDate() + i);
                    const isToday = d.toDateString() === new Date().toDateString();
                    return (
                        <div key={day} style={{ padding: 15, textAlign: 'center', background: isToday ? styles.today : styles.cardHeader, fontWeight: 600, borderBottom: `1px solid ${styles.border}`, color: styles.text }}>
                            {day} <span style={{ opacity: 0.6, fontSize: '0.8em' }}>{d.getDate()}</span>
                        </div>
                    )
                })}

                {/* Time Rows */}
                {hours.map(hour => (
                    <React.Fragment key={hour}>
                        {/* Time Label */}
                        <div style={{ padding: 10, textAlign: 'right', fontSize: '0.8rem', opacity: 0.5, borderRight: `1px solid ${styles.border}`, borderBottom: `1px solid ${styles.border}`, background: styles.bg }}>
                            {hour}:00
                        </div>

                        {/* Slots */}
                        {days.map((_, dayIndex) => {
                            // Find meeting
                            const slotDate = new Date(weekStart);
                            slotDate.setDate(slotDate.getDate() + dayIndex);

                            // Check matches
                            const meeting = meetings.find(m => {
                                const mDate = new Date(m.start);
                                return mDate.getDate() === slotDate.getDate() &&
                                    mDate.getMonth() === slotDate.getMonth() &&
                                    mDate.getHours() === hour;
                            });

                            return (
                                <div key={dayIndex}
                                    onClick={() => !meeting && handleCreateSlot(dayIndex, hour)}
                                    style={{
                                        borderRight: `1px solid ${styles.border}`,
                                        borderBottom: `1px solid ${styles.border}`,
                                        position: 'relative', cursor: meeting ? 'default' : 'pointer',
                                        transition: 'background 0.2s',
                                        background: (!meeting && hoveredSlot?.dayIndex === dayIndex && hoveredSlot?.hour === hour) ? styles.cardBg : styles.bg
                                    }}
                                    onMouseEnter={() => !meeting && setHoveredSlot({ dayIndex, hour })}
                                    onMouseLeave={() => setHoveredSlot(null)}
                                // Removed inline hover styles to use state
                                >
                                    {!meeting && hoveredSlot?.dayIndex === dayIndex && hoveredSlot?.hour === hour && (
                                        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: styles.text, opacity: 0.5, fontSize: '1.5rem', userSelect: 'none' }}>+</div>
                                    )}

                                    {meeting && (
                                        <div
                                            onClick={() => setSelectedMeeting(meeting)}
                                            className="animate-scale-in" style={{
                                                position: 'absolute', inset: 2, borderRadius: 8,
                                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                padding: 8, fontSize: '0.8rem', overflow: 'hidden',
                                                boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                                            }}>
                                            <div style={{ fontWeight: 600 }}>{meeting.title}</div>
                                            <div onClick={(e) => handleDelete(meeting.id, e)} style={{ position: 'absolute', top: 2, right: 5, cursor: 'pointer', opacity: 0.7 }}>&times;</div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

// --- MAIN PAGE ---
export default function CommercialLaunchpadPage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    // States
    const [pin, setPin] = useState('');
    const [showPin, setShowPin] = useState(false);

    // Secret Zone States
    const [secretPwd, setSecretPwd] = useState('');
    const [isRestrictedUnlocked, setIsRestrictedUnlocked] = useState(false);

    const [theme, setTheme] = useState(localStorage.getItem('commercial_theme') || 'light');

    useEffect(() => {
        localStorage.setItem('commercial_theme', theme);
    }, [theme]);
    const [showQR, setShowQR] = useState(false);
    const [showWhiteboard, setShowWhiteboard] = useState(false);
    const [showCheckIn, setShowCheckIn] = useState(false);
    const [showMemo, setShowMemo] = useState(false);
    const [showPortfolio, setShowPortfolio] = useState(false);
    const [nextMeeting, setNextMeeting] = useState(null);
    const [idleTime, setIdleTime] = useState(0);
    const [showScreensaver, setShowScreensaver] = useState(false);

    const [showCalc, setShowCalc] = useState(false);
    const [showChecklist, setShowChecklist] = useState(false);
    const [showDecision, setShowDecision] = useState(false);
    const [showTimer, setShowTimer] = useState(false);
    const [showPostIt, setShowPostIt] = useState(false);
    const [showFocus, setShowFocus] = useState(false);

    const [showObjection, setShowObjection] = useState(false);
    const [showIceBreaker, setShowIceBreaker] = useState(false);
    const [showConverter, setShowConverter] = useState(false);
    const [showNextSlot, setShowNextSlot] = useState(false);
    const [showZen, setShowZen] = useState(false);
    // const [showGong, setShowGong] = useState(false); // Replaced by Soundboard

    const [showWeather, setShowWeather] = useState(false);
    const [showWorldClock, setShowWorldClock] = useState(false);
    const [showDiscount, setShowDiscount] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showSoundboard, setShowSoundboard] = useState(false);
    const [showQuote, setShowQuote] = useState(false);

    // Stores the last person who checked in to link the note
    // Stores the last person who checked in to link the note
    const [lastProspect, setLastProspect] = useState(null);

    // Profile Data
    const [profile, setProfile] = useState(null);
    useEffect(() => {
        if (user?.id) {
            pb.collection('commercial_profiles').getFirstListItem(`user="${user.id}"`)
                .then(res => setProfile(res))
                .catch(() => { });
        }
    }, [user?.id]);

    // Idle Timer for Screensaver
    useEffect(() => {
        const reset = () => {
            setIdleTime(0);
            setShowScreensaver(false);
        };
        const events = ['mousemove', 'mousedown', 'touchstart', 'keydown'];
        events.forEach(e => window.addEventListener(e, reset));

        const tick = setInterval(() => {
            setIdleTime(t => {
                if (t > 120) { // 120 seconds = 2 mins
                    setShowScreensaver(true);
                }
                return t + 1;
            });
        }, 1000);

        return () => {
            clearInterval(tick);
            events.forEach(e => window.removeEventListener(e, reset));
        };
    }, []);

    // Fetch Next Meeting
    useEffect(() => {
        async function fetchMeeting() {
            try {
                // Fetch basic meetings for user where start > now
                const now = new Date().toISOString();
                const res = await pb.collection('meetings').getList(1, 1, {
                    filter: `created_by = "${user.id}" && start > "${now}"`, // Assuming fields
                    sort: 'start'
                });
                if (res.items.length > 0) setNextMeeting(res.items[0]);
            } catch (err) { /* silent fail or demo mode */ }
        }
        if (user?.id) fetchMeeting();
    }, [user?.id]);

    // Themes logic
    const themes = {
        dark: { bg: '#0f172a', text: 'white', card: 'rgba(255,255,255,0.05)', title: 'Sombre', btnBg: 'rgba(255,255,255,0.05)', btnBorder: 'rgba(255,255,255,0.1)', modalBg: '#1e293b', modalText: 'white', modalBorder: 'rgba(255,255,255,0.1)' },
        light: { bg: '#f8fafc', text: '#1e293b', card: 'white', border: 'rgba(0,0,0,0.05)', shadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)', title: 'Clair', btnBg: 'white', btnBorder: 'rgba(0,0,0,0.1)', modalBg: 'white', modalText: '#1e293b', modalBorder: 'rgba(0,0,0,0.1)' },
        culinary: { bg: 'url("https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=2000&q=80")', text: 'white', card: 'rgba(0,0,0,0.6)', title: 'Restaurant' },
        tech: { bg: 'url("https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=2000&q=80")', text: 'white', card: 'rgba(0,0,0,0.6)', title: 'Tech' },
        construction: { bg: 'url("https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=2000&q=80")', text: 'white', card: 'rgba(0,0,0,0.6)', title: 'BTP' },
        realestate: { bg: 'url("https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=2000&q=80")', text: 'white', card: 'rgba(0,0,0,0.6)', title: 'Immo' },
    };
    const currentTheme = themes[theme] || themes.dark;

    // Fake screensaver images (Portfolio samples)
    const screensaverImages = [
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80',
        'https://images.unsplash.com/photo-1522542550221-31fd19575a2d?auto=format&fit=crop&w=1600&q=80',
        'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=80'
    ];

    const APPS = [
        { id: 'checkin', title: 'Accueil Visiteur', icon: '🛎️', route: '#checkin', color: 'from-pink-500 to-rose-500', category: 'Accueil' },
        { id: 'configurator', title: 'Nouveau Projet', icon: '⚡', route: '/commercial/configurator', color: 'from-emerald-400 to-cyan-600', category: 'Accueil' },
        { id: 'booking', title: 'Calendrier', icon: '📅', route: `/booking/${user?.id}`, color: 'from-blue-400 to-blue-600', category: 'Accueil' },

        { id: 'portfolio', title: 'Portfolio', icon: '🎨', route: '/realisations', color: 'from-purple-500 to-indigo-600', category: 'Portfolio' },
        { id: 'demo', title: 'Démo Admin', icon: '🎭', route: '/commercial/demo', color: 'from-violet-500 to-fuchsia-600', category: 'Portfolio' },
        { id: 'moodboard', title: 'Design Match', icon: '🎨', route: '/commercial/moodboard', color: 'from-pink-400 to-rose-400', category: 'Création & Design' },
        { id: 'diagnostic', title: 'Check-Up Site', icon: '🩺', route: '/commercial/diagnostic', color: 'from-blue-400 to-indigo-500', category: 'Discussion' },
        { id: 'sign', title: 'Signature', icon: '✒️', route: '/commercial/sign', color: 'from-yellow-400 to-amber-500' },
        { id: 'roadmap', title: 'Roadmap', icon: '🗓️', route: '/commercial/roadmap', color: 'from-teal-400 to-emerald-500', category: 'Discussion' },
        { id: 'video', title: 'Vidéo Maton', icon: '📹', route: '/commercial/video', color: 'from-fuchsia-500 to-pink-600' },
        { id: 'wheel', title: 'Cadeaux', icon: '🎁', route: '/commercial/wheel', color: 'from-yellow-400 to-orange-500', category: 'Bonus' },
        { id: 'cash', title: 'Cash Machine', icon: '💸', route: '/commercial/cash-machine', color: 'from-green-500 to-emerald-600', restricted: true },
        { id: 'spy', title: 'Espion Digital', icon: '🕵️‍♂️', route: '/commercial/spy', color: 'from-slate-600 to-zinc-700', restricted: true },
        { id: 'architect', title: 'Architecte', icon: '🏗️', route: '/commercial/architect', color: 'from-blue-600 to-indigo-700', category: 'Création & Design' },
        { id: 'brief', title: 'Assistant Brief', icon: '🤖', route: '/commercial/brief', color: 'from-gray-600 to-slate-700', restricted: true },
        { id: 'ice', title: 'Ice Breaker', icon: '🧊', route: '/commercial/icebreaker', color: 'from-cyan-400 to-blue-400' },
        { id: 'time', title: 'Time Master', icon: '🗓️', route: '/commercial/time-master', color: 'from-pink-500 to-rose-500', restricted: true },
        { id: 'memo', title: 'Smart Memo', icon: '📝', route: '/commercial/memo', color: 'from-stone-500 to-stone-700', category: 'Création & Design' },
        { id: 'sorter', title: 'Le Trieur', icon: '🗂️', route: '/commercial/sorter', color: 'from-amber-500 to-orange-600', restricted: true },
        { id: 'echo', title: 'L\'Écho', icon: '☁️', route: '/commercial/echo', color: 'from-sky-400 to-indigo-400', restricted: true },
        { id: 'vision', title: 'Vision', icon: '🔮', route: '/commercial/vision', color: 'from-fuchsia-600 to-pink-600', restricted: true },
        { id: 'speed', title: 'Speed Racer', icon: '🏎️', route: '/commercial/speed', color: 'from-orange-500 to-red-500', restricted: true },
        { id: 'bundles', title: 'Sommelier', icon: '🍷', route: '/commercial/bundles', color: 'from-indigo-500 to-violet-500', category: 'Portfolio' },
        { id: 'night', title: '24/7', icon: '🌙', route: '/commercial/night-worker', color: 'from-slate-800 to-black', restricted: true },
        { id: 'security', title: 'Bouclier', icon: '🛡️', route: '/commercial/security', color: 'from-blue-600 to-indigo-800', restricted: true },
        { id: 'zen', title: 'Zen Mode', icon: '🧘', route: '#', color: 'from-teal-400 to-emerald-500', restricted: true, quickTool: true },
        { id: 'club', title: 'Club Privé', icon: '🗝️', route: '/commercial/club', color: 'from-amber-400 to-yellow-600', category: 'Bonus' },
        { id: 'referral', title: 'Parrainage', icon: '🤝', route: '/commercial/referral', color: 'from-blue-600 to-cyan-500', category: 'Bonus' },
        { id: 'greenlight', title: 'Feu Vert', icon: '🚦', route: '/commercial/green-light', color: 'from-green-500 to-emerald-700', restricted: true },
        { id: 'studio', title: 'Studio', icon: '🎩', route: '/commercial/studio', color: 'from-violet-600 to-purple-600', restricted: true },
        { id: 'domain', title: 'Domaine', icon: '🎯', route: '/commercial/domain', color: 'from-orange-500 to-amber-500', restricted: true },
        { id: 'trends', title: 'Trends', icon: '📈', route: '/commercial/trends', color: 'from-blue-500 to-indigo-600', restricted: true },
        { id: 'production', title: 'Rush', icon: '🚧', route: '/commercial/production', color: 'from-red-600 to-orange-700', restricted: true },
        { id: 'buzz', title: 'Générateur Buzz', icon: '📣', route: '/commercial/buzz', color: 'from-purple-500 to-indigo-500', restricted: true },
        { id: 'qt-calc', title: 'Calculatrice', icon: '🧮', route: '#', color: 'from-red-400 to-orange-500', restricted: true, quickTool: true },
        { id: 'qt-pwd', title: 'Générateur MDP', icon: '🔐', route: '#', color: 'from-green-400 to-teal-500', restricted: true, quickTool: true },
        { id: 'qt-sound', title: 'Soundboard', icon: '🔊', route: '#', color: 'from-yellow-400 to-amber-500', restricted: true, quickTool: true },
        { id: 'qt-weather', title: 'Météo Client', icon: '🌤️', route: '#', color: 'from-sky-400 to-blue-500', restricted: true, quickTool: true },
        { id: 'qt-clock', title: 'Horloge Monde', icon: '🌍', route: '#', color: 'from-indigo-400 to-purple-500', restricted: true, quickTool: true },
        { id: 'battlecards', title: 'Battlecards', icon: '⚔️', route: '/commercial/battlecards', color: 'from-red-500 to-rose-600', category: 'Discussion' },
    ];

    // Separate apps
    const publicApps = APPS.filter(app => !app.restricted);
    const standardPrivateApps = APPS.filter(app => app.restricted && !app.quickTool);
    const quickToolApps = APPS.filter(app => app.restricted && app.quickTool);

    const handleSecretSubmit = (e) => {
        e.preventDefault();
        if (secretPwd === 'test1234') {
            setIsRestrictedUnlocked(true);
            setSecretPwd('');
        } else {
            alert('Mot de passe Zone Secrète incorrect');
        }
    };

    const handlePinSubmit = (e) => {
        e.preventDefault();
        if (pin === '0000') navigate('/commercial');
        else { alert('Code PIN incorrect (0000)'); setPin(''); }
    };

    const handleAppClick = (app) => {
        if (app.id === 'checkin') setShowCheckIn(true);
        else if (app.id === 'qt-calc') setShowDiscount(true);
        else if (app.id === 'qt-pwd') setShowPassword(true);
        else if (app.id === 'qt-sound') setShowSoundboard(true);
        else if (app.id === 'qt-weather') setShowWeather(true);
        else if (app.id === 'qt-clock') setShowWorldClock(true);
        else if (app.id === 'zen') setShowZen(true);
        else if (app.id === 'zen') setShowZen(true);
        else if (app.id === 'booking') navigate('/commercial/launchpad/agenda');
        else if (app.id === 'portfolio') setShowPortfolio(true);
        else navigate(app.route);
    };

    const handleCheckIn = async (name, email) => {
        setLastProspect({ name, email });
        try {
            // Check existence
            try {
                await pb.collection('customers').getFirstListItem(`email="${email}"`);
            } catch {
                // Create minimal prospect
                await pb.collection('customers').create({
                    company: 'Prospect (Check-in)',
                    name: name,
                    email: email,
                    status: 'lead',
                    commercial_id: user.id,
                    created_by: user.id
                });
            }
        } catch (err) { console.error(err); }
    };

    if (showScreensaver) return <ScreenSaver images={screensaverImages} onClose={() => setShowScreensaver(false)} />;

    return (
        <div style={{
            height: '100vh', width: '100vw', overflow: 'hidden',
            background: currentTheme.bg.startsWith('url') ? currentTheme.bg : currentTheme.bg,
            backgroundSize: 'cover', backgroundPosition: 'center',
            color: currentTheme.text, fontFamily: 'Inter, sans-serif',
            display: 'flex', transition: 'background 0.5s',
            '--theme-border': currentTheme.border || 'rgba(255,255,255,0.05)',
            '--theme-shadow': currentTheme.shadow || 'none',
            '--theme-btn-bg': currentTheme.btnBg || 'rgba(255,255,255,0.05)',
            '--theme-btn-border': currentTheme.btnBorder || 'rgba(255,255,255,0.1)',
            '--theme-modal-bg': currentTheme.modalBg || '#1e293b',
            '--theme-modal-text': currentTheme.modalText || 'white',
            '--theme-modal-border': currentTheme.modalBorder || 'rgba(255,255,255,0.1)'
        }}>
            {/* Overlay if image bg */}
            {currentTheme.bg.startsWith('url') && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 0 }} />}

            {showPortfolio && <PortfolioModal onClose={() => setShowPortfolio(false)} theme={theme} />}

            {/* --- SIDEBAR --- */}
            <div className="sidebar-scroll" style={{
                width: 320, background: 'rgba(255,255,255,0.03)', borderRight: '1px solid rgba(255,255,255,0.1)',
                padding: 40, display: 'flex', flexDirection: 'column', zIndex: 1,
                backdropFilter: 'blur(30px)', // Glassmorphism heavily used
                overflowY: 'auto', height: '100%'
            }}>
                {/* Profile */}
                {/* Profile */}
                <div style={{ marginBottom: 40, textAlign: 'center' }}>
                    <div style={{ width: 100, height: 100, borderRadius: '50%', background: '#334155', margin: '0 auto 20px', overflow: 'hidden', border: '3px solid ' + (profile?.level >= 1 ? (profile?.specialty === 'Expert Digital' ? '#3b82f6' : '#10b981') : 'rgba(255,255,255,0.2)'), boxShadow: profile?.level >= 1 ? '0 0 20px rgba(255,255,255, 0.1)' : 'none', position: 'relative' }}>
                        {profile?.photo ? (
                            <img src={pb.files.getUrl(profile, profile.photo)} alt="Agent" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            user?.avatar ? <img src={pb.files.getUrl(user, user.avatar)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: '2.5rem' }}>👤</div>
                        )}
                    </div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0 0 5px 0' }}>
                        {profile ? `${profile.firstname} ${profile.lastname}` : (user?.name || 'Commercial')}
                        {profile?.level >= 1 && <span title="Accrédité" style={{ marginLeft: 6, fontSize: '1rem' }}>✅</span>}
                    </h2>
                    {profile ? (
                        <div style={{ display: 'inline-block', background: profile.specialty === 'Expert Digital' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(16, 185, 129, 0.2)', color: profile.specialty === 'Expert Digital' ? '#60a5fa' : '#86efac', padding: '4px 10px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, marginBottom: 10, border: '1px solid currentColor' }}>
                            {profile.specialty} • Niv. {profile.level}
                        </div>
                    ) : (
                        <p style={{ opacity: 0.7, margin: 0, fontSize: '0.9rem', letterSpacing: '1px', textTransform: 'uppercase' }}>Expert Digital</p>
                    )}
                </div>

                {/* Unlock */}
                <div style={{ marginBottom: 40 }}>
                    <button onClick={() => setShowPin(!showPin)} style={{ background: 'var(--theme-btn-bg)', border: '1px solid var(--theme-btn-border)', color: 'inherit', padding: '12px 20px', borderRadius: 8, cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.9rem' }}>
                        <span>🔒</span> Espace Privé
                    </button>
                    {showPin && (
                        <form onSubmit={handlePinSubmit} style={{ marginTop: 16 }}>
                            <input autoFocus type="password" placeholder="Code PIN (0000)" value={pin} onChange={e => setPin(e.target.value)} style={{ width: '100%', padding: '12px', background: 'var(--theme-btn-border)', border: '1px solid var(--theme-border)', borderRadius: 8, color: 'inherit', outline: 'none', textAlign: 'center', letterSpacing: 4 }} />
                        </form>
                    )}
                </div>

                {/* Theme Selector */}
                <div style={{ marginBottom: 40 }}>
                    <h3 style={{ fontSize: '0.9rem', textTransform: 'uppercase', opacity: 0.5, marginBottom: 16 }}>Thèmes</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        <button onClick={() => setTheme('dark')} style={{ width: 30, height: 30, borderRadius: '50%', background: '#0f172a', border: `2px solid ${theme === 'dark' ? '#3b82f6' : 'rgba(255,255,255,0.2)'}`, cursor: 'pointer' }} title="Sombre" />
                        <button onClick={() => setTheme('light')} style={{ width: 30, height: 30, borderRadius: '50%', background: '#f1f5f9', border: `2px solid ${theme === 'light' ? '#3b82f6' : 'rgba(255,255,255,0.2)'}`, cursor: 'pointer' }} title="Clair" />
                        <button onClick={() => setTheme('culinary')} style={{ width: 30, height: 30, borderRadius: '50%', background: '#ef4444', border: `2px solid ${theme === 'culinary' ? '#3b82f6' : 'rgba(255,255,255,0.2)'}`, cursor: 'pointer' }} title="Restaurant" />
                        <button onClick={() => setTheme('tech')} style={{ width: 30, height: 30, borderRadius: '50%', background: '#3b82f6', border: `2px solid ${theme === 'tech' ? '#3b82f6' : 'rgba(255,255,255,0.2)'}`, cursor: 'pointer' }} title="Tech" />
                        <button onClick={() => setTheme('construction')} style={{ width: 30, height: 30, borderRadius: '50%', background: '#f59e0b', border: `2px solid ${theme === 'construction' ? '#3b82f6' : 'rgba(255,255,255,0.2)'}`, cursor: 'pointer' }} title="BTP" />
                        <button onClick={() => setTheme('realestate')} style={{ width: 30, height: 30, borderRadius: '50%', background: '#10b981', border: `2px solid ${theme === 'realestate' ? '#3b82f6' : 'rgba(255,255,255,0.2)'}`, cursor: 'pointer' }} title="Immobilier" />
                    </div>
                </div>



                <div style={{ marginBottom: 40 }}>
                    <h3 style={{ fontSize: '0.9rem', textTransform: 'uppercase', opacity: 0.5, marginBottom: 16 }}>Outils Rapides</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                        <button onClick={() => setShowQR(true)} className="quick-btn" title="QR Code">📱</button>
                        <button onClick={() => setShowWhiteboard(true)} className="quick-btn" title="Tableau Blanc">✏️</button>
                        <button onClick={() => setShowMemo(true)} className="quick-btn" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', borderColor: 'rgba(239, 68, 68, 0.3)' }} title="Note Vocale">🎙️</button>

                        <button onClick={() => setShowCalc(!showCalc)} className="quick-btn" title="Calculatrice">🧮</button>
                        <button onClick={() => setShowChecklist(!showChecklist)} className="quick-btn" title="Checklist">✅</button>
                        <button onClick={() => setShowDecision(!showDecision)} className="quick-btn" title="Décision">🎲</button>

                        <button onClick={() => setShowTimer(!showTimer)} className="quick-btn" title="Chrono">⏳</button>
                        <button onClick={() => setShowPostIt(!showPostIt)} className="quick-btn" title="Post-it">📌</button>
                        <button onClick={() => setShowFocus(!showFocus)} className="quick-btn" title="Mode Focus">🔦</button>

                        <button onClick={() => setShowObjection(!showObjection)} className="quick-btn" title="Objection Killer">🥊</button>
                        <button onClick={() => setShowIceBreaker(!showIceBreaker)} className="quick-btn" title="Ice Breakers">🧊</button>
                        <button onClick={() => setShowConverter(!showConverter)} className="quick-btn" title="Convertisseur">🔄</button>

                        <button onClick={() => navigate('/commercial/launchpad/agenda')} className="quick-btn" title="Prochain RDV">📆</button>




                        <button onClick={() => setShowDiscount(!showDiscount)} className="quick-btn" title="Calc. Remise">📉</button>

                        <button onClick={() => setShowQuote(!showQuote)} className="quick-btn" title="Citation du Jour">🦁</button>
                    </div>
                </div>


            </div>

            {/* --- MAIN AREA --- */}
            <div style={{ flex: 1, padding: 60, display: 'flex', flexDirection: 'column', zIndex: 1, overflowY: 'auto' }}>
                {/* Smart Briefing */}
                {nextMeeting && (
                    <div className="animate-slide-down" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '16px 24px', borderRadius: 16, display: 'inline-flex', alignItems: 'center', gap: 20, marginBottom: 40, border: '1px solid rgba(255,255,255,0.1)', maxWidth: 600, flexShrink: 0 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 10px #4ade80' }} />
                        <div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.7, textTransform: 'uppercase' }}>Prochain Rendez-vous</div>
                            <div style={{ fontWeight: 600 }}>{new Date(nextMeeting.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {nextMeeting.title}</div>
                        </div>
                        <button style={{ marginLeft: 'auto', background: 'white', color: 'black', border: 'none', padding: '8px 16px', borderRadius: 50, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/commercial/configurator')}>
                            Ouvrir le dossier
                        </button>
                    </div>
                )}

                <div style={{ paddingBottom: 60 }}>
                    <h1 style={{ fontSize: '3rem', fontWeight: 800, marginBottom: 10 }}>Bonjour, {profile ? profile.firstname : user?.name?.split(' ')[0]} 👋</h1>
                    <p style={{ fontSize: '1.2rem', opacity: 0.7, marginBottom: 40 }}>Tout est prêt. Bonne vente !</p>

                    <div style={{ maxWidth: 1200 }}>
                        {/* CATEGORY: ACCUEIL */}
                        <div style={{ marginBottom: 40 }}>
                            <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', opacity: 0.5, marginBottom: 20, letterSpacing: '2px' }}>Accueil</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 30 }}>
                                {publicApps.filter(app => app.category === 'Accueil').map(app => (
                                    <div key={app.id} onClick={() => handleAppClick(app)} className="launch-card-uniform" style={{ background: currentTheme.card }}>
                                        <div className={`icon-bg bg-gradient-to-br ${app.color}`} style={{ marginBottom: 40 }}>{app.icon}</div>
                                        <h3 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '20px 0 16px 0' }}>{app.title}</h3>
                                        <div className="launch-action" style={{ color: currentTheme.text === 'white' ? '#94a3b8' : '#64748b' }}>
                                            Lancer l'application <span style={{ marginLeft: 6 }}>→</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CATEGORY: PORTFOLIO */}
                        <div style={{ marginBottom: 40 }}>
                            <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', opacity: 0.5, marginBottom: 20, letterSpacing: '2px' }}>Portfolio</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 30 }}>
                                {publicApps.filter(app => app.category === 'Portfolio').map(app => (
                                    <div key={app.id} onClick={() => handleAppClick(app)} className="launch-card-uniform" style={{ background: currentTheme.card }}>
                                        <div className={`icon-bg bg-gradient-to-br ${app.color}`} style={{ marginBottom: 40 }}>{app.icon}</div>
                                        <h3 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '20px 0 16px 0' }}>{app.title}</h3>
                                        <div className="launch-action" style={{ color: currentTheme.text === 'white' ? '#94a3b8' : '#64748b' }}>
                                            Lancer l'application <span style={{ marginLeft: 6 }}>→</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CATEGORY: DISCUSSION */}
                        <div style={{ marginBottom: 40 }}>
                            <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', opacity: 0.5, marginBottom: 20, letterSpacing: '2px' }}>Discussion</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 30 }}>
                                {publicApps.filter(app => app.category === 'Discussion').map(app => (
                                    <div key={app.id} onClick={() => handleAppClick(app)} className="launch-card-uniform" style={{ background: currentTheme.card }}>
                                        <div className={`icon-bg bg-gradient-to-br ${app.color}`} style={{ marginBottom: 40 }}>{app.icon}</div>
                                        <h3 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '20px 0 16px 0' }}>{app.title}</h3>
                                        <div className="launch-action" style={{ color: currentTheme.text === 'white' ? '#94a3b8' : '#64748b' }}>
                                            Lancer l'application <span style={{ marginLeft: 6 }}>→</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CATEGORY: CREATION & DESIGN */}
                        <div style={{ marginBottom: 40 }}>
                            <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', opacity: 0.5, marginBottom: 20, letterSpacing: '2px' }}>Création & Design</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 30 }}>
                                {publicApps.filter(app => app.category === 'Création & Design').map(app => (
                                    <div key={app.id} onClick={() => handleAppClick(app)} className="launch-card-uniform" style={{ background: currentTheme.card }}>
                                        <div className={`icon-bg bg-gradient-to-br ${app.color}`} style={{ marginBottom: 40 }}>{app.icon}</div>
                                        <h3 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '20px 0 16px 0' }}>{app.title}</h3>
                                        <div className="launch-action" style={{ color: currentTheme.text === 'white' ? '#94a3b8' : '#64748b' }}>
                                            Lancer l'application <span style={{ marginLeft: 6 }}>→</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CATEGORY: BONUS */}
                        <div style={{ marginBottom: 40 }}>
                            <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', opacity: 0.5, marginBottom: 20, letterSpacing: '2px' }}>Bonus</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 30 }}>
                                {publicApps.filter(app => app.category === 'Bonus').map(app => (
                                    <div key={app.id} onClick={() => handleAppClick(app)} className="launch-card-uniform" style={{ background: currentTheme.card }}>
                                        <div className={`icon-bg bg-gradient-to-br ${app.color}`} style={{ marginBottom: 40 }}>{app.icon}</div>
                                        <h3 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '20px 0 16px 0' }}>{app.title}</h3>
                                        <div className="launch-action" style={{ color: currentTheme.text === 'white' ? '#94a3b8' : '#64748b' }}>
                                            Lancer l'application <span style={{ marginLeft: 6 }}>→</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CATEGORY: AUTRES OUTILS */}
                        {publicApps.filter(app => !app.category).length > 0 && (
                            <div style={{ marginBottom: 40 }}>
                                <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', opacity: 0.5, marginBottom: 20, letterSpacing: '2px' }}>Autres Outils</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 30 }}>
                                    {publicApps.filter(app => !app.category).map(app => (
                                        <div key={app.id} onClick={() => handleAppClick(app)} className="launch-card-uniform" style={{ background: currentTheme.card }}>
                                            <div className={`icon-bg bg-gradient-to-br ${app.color}`} style={{ marginBottom: 40 }}>{app.icon}</div>
                                            <h3 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '20px 0 16px 0' }}>{app.title}</h3>
                                            <div className="launch-action" style={{ color: currentTheme.text === 'white' ? '#94a3b8' : '#64748b' }}>
                                                Lancer l'application <span style={{ marginLeft: 6 }}>→</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* --- SECRET ZONE --- */}
                    <div style={{ marginTop: 80, borderTop: '1px solid var(--theme-border)', paddingTop: 40, maxWidth: 1200, paddingBottom: 60 }}>
                        {!isRestrictedUnlocked ? (
                            <div style={{ textAlign: 'center', padding: 40, background: 'var(--theme-btn-border)', borderRadius: 24, border: '1px dashed var(--theme-border)' }}>
                                <div style={{ fontSize: '3rem', marginBottom: 20 }}>🔒</div>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 10 }}>Zone Secrète</h3>
                                <p style={{ opacity: 0.6, marginBottom: 20 }}>Mot de passe requis pour cette section.</p>

                                <form onSubmit={handleSecretSubmit} style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                                    <input
                                        type="password"
                                        placeholder="Mot de passe..."
                                        value={secretPwd}
                                        onChange={e => setSecretPwd(e.target.value)}
                                        style={{ padding: '12px 20px', borderRadius: 50, border: '1px solid var(--theme-border)', background: 'var(--theme-btn-border)', color: 'inherit', outline: 'none', width: 200 }}
                                    />
                                    <button type="submit" style={{ padding: '12px 30px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 50, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 6px rgba(59, 130, 246, 0.4)', transition: 'all 0.2s' }}>
                                        Déverrouiller
                                    </button>
                                </form>
                            </div>
                        ) : (
                            <div className="animate-slide-down">
                                <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span>🔓</span> Zone Secrète Ouverte
                                </h3>

                                {/* Standard Restricted Apps */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 30, marginBottom: 40 }}>
                                    {standardPrivateApps.map(app => (
                                        <div key={app.id} onClick={() => handleAppClick(app)} className="launch-card-uniform" style={{ background: currentTheme.card }}>
                                            <div className={`icon-bg bg-gradient-to-br ${app.color}`} style={{ marginBottom: 40 }}>{app.icon}</div>
                                            <h3 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '20px 0 16px 0' }}>{app.title}</h3>
                                            <div className="launch-action" style={{ color: currentTheme.text === 'white' ? '#94a3b8' : '#64748b' }}>
                                                Accéder <span style={{ marginLeft: 6 }}>→</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Quick Tools Frame */}
                                {quickToolApps.length > 0 && (
                                    <div style={{ background: 'var(--theme-btn-border)', padding: 24, borderRadius: 20, border: '1px dashed var(--theme-border)' }}>
                                        <h4 style={{ textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.9rem', opacity: 0.6, marginBottom: 20 }}>⚡ Outils Rapides</h4>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                                            {quickToolApps.map(app => (
                                                <div key={app.id} onClick={() => handleAppClick(app)} className="launch-card-compact" style={{ background: currentTheme.card }}>
                                                    <div className={`icon-bg-small bg-gradient-to-br ${app.color}`}>{app.icon}</div>
                                                    <span style={{ fontWeight: 600, fontSize: '1rem' }}>{app.title}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- MODALS --- */}
            {showQR && <QRModal user={user} onClose={() => setShowQR(false)} />}
            {showWhiteboard && <WhiteboardModal onClose={() => setShowWhiteboard(false)} />}
            {showCheckIn && <CheckInModal onCheckIn={handleCheckIn} onClose={() => setShowCheckIn(false)} />}
            {showMemo && <MemoModal lastProspect={lastProspect} user={user} onClose={() => setShowMemo(false)} />}

            {showCalc && <CalculatorModal onClose={() => setShowCalc(false)} />}
            {showChecklist && <ChecklistModal onClose={() => setShowChecklist(false)} />}
            {showDecision && <DecisionModal onClose={() => setShowDecision(false)} />}
            {showTimer && <TimerModal onClose={() => setShowTimer(false)} />}
            {showPostIt && <PostItModal onClose={() => setShowPostIt(false)} />}
            {showFocus && <FocusOverlay onClose={() => setShowFocus(false)} />}

            {showObjection && <ObjectionModal onClose={() => setShowObjection(false)} />}
            {showIceBreaker && <IceBreakerModal onClose={() => setShowIceBreaker(false)} />}
            {showConverter && <ConverterModal onClose={() => setShowConverter(false)} />}
            {showNextSlot && <NextSlotModal onClose={() => setShowNextSlot(false)} />}
            {showZen && <ZenModal onClose={() => setShowZen(false)} />}
            {/* {showGong && <GongModal onClose={() => setShowGong(false)} />} */}

            {showWeather && <WeatherModal onClose={() => setShowWeather(false)} />}
            {showWorldClock && <WorldClockModal onClose={() => setShowWorldClock(false)} />}
            {showDiscount && <DiscountModal onClose={() => setShowDiscount(false)} />}
            {showPassword && <PasswordModal onClose={() => setShowPassword(false)} />}
            {showSoundboard && <SoundboardModal onClose={() => setShowSoundboard(false)} />}
            {showQuote && <QuoteModal onClose={() => setShowQuote(false)} />}



            <style>{`
                .quick-btn {
                    padding: 16px; border-radius: 12px;
                    border: 1px solid var(--theme-btn-border);
                    background: var(--theme-btn-bg);
                    color: inherit; cursor: pointer;
                    font-weight: 600; text-align: center; transition: all 0.2s;
                    box-shadow: var(--theme-shadow);
                }
                .quick-btn:hover {
                    filter: brightness(1.1) contrast(1.1);
                    transform: translateY(-2px);
                }
                
                .launch-card-uniform {
                    border: 1px solid var(--theme-border);
                    box-shadow: var(--theme-shadow);
                    border-radius: 24px; padding: 32px; cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex; flex-direction: column; align-items: flex-start;
                    position: relative; overflow: hidden; backdrop-filter: blur(10px);
                }
                .launch-card-uniform:hover {
                    transform: translateY(-8px);
                    background: rgba(255,255,255,0.15) !important;
                    box-shadow: 0 20px 40px -10px rgba(0,0,0,0.3);
                    border-color: rgba(255,255,255,0.3);
                }
                .launch-card-uniform:hover .launch-action span:last-child {
                    transform: translateX(4px);
                    transition: transform 0.2s ease;
                    display: inline-block;
                }
                .launch-action {
                    font-size: 0.9rem; opacity: 0.8; margin-top: auto;
                    width: 100%; text-align: left;
                    transition: opacity 0.2s;
                }
                .launch-card-uniform:hover .launch-action { opacity: 1; }
                .launch-card-uniform .icon-bg {
                    width: 64px; height: 64px; border-radius: 16px;
                    display: grid; place-items: center; font-size: 2rem;
                    margin-bottom: 60px;
                    background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
                }

                .animate-slide-down { animation: slideDown 0.5s ease-out; }
                @keyframes slideDown { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

                /* Tailwind-like gradients polyfill */
                .from-purple-500 { --tw-gradient-from: #a855f7; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(168, 85, 247, 0)); }
                .to-indigo-600 { --tw-gradient-to: #4f46e5; }
                .from-emerald-400 { --tw-gradient-from: #34d399; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(168, 85, 247, 0)); }
                .to-cyan-600 { --tw-gradient-to: #0891b2; }
                .from-orange-400 { --tw-gradient-from: #fb923c; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(168, 85, 247, 0)); }
                .to-red-500 { --tw-gradient-to: #ef4444; }
                .from-blue-400 { --tw-gradient-from: #60a5fa; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(168, 85, 247, 0)); }
                .to-blue-600 { --tw-gradient-to: #2563eb; }
                .from-pink-500 { --tw-gradient-from: #ec4899; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(168, 85, 247, 0)); }
                .to-rose-500 { --tw-gradient-to: #f43f5e; }
                .from-violet-500 { --tw-gradient-from: #8b5cf6; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(168, 85, 247, 0)); }
                .to-fuchsia-600 { --tw-gradient-to: #c026d3; }
                .bg-gradient-to-br { background-image: linear-gradient(to bottom right, var(--tw-gradient-stops)); }
                
                .launch-card-compact {
                    display: flex; alignItems: center; gap: 12px;
                    padding: 12px 20px 12px 12px; border-radius: 12px;
                    border: 1px solid var(--theme-border);
                    box-shadow: var(--theme-shadow);
                    cursor: pointer; transition: all 0.2s;
                    min-width: 180px;
                }
                .launch-card-compact:hover {
                    background: rgba(255,255,255,0.1) !important;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                }
                .icon-bg-small {
                    width: 36px; height: 36px; border-radius: 8px;
                    display: grid; place-items: center; font-size: 1.2rem;
                }

                /* Custom Scrollbar for Sidebar */
                .sidebar-scroll::-webkit-scrollbar { width: 6px; }
                .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
                .sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 10px; }
                .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.3); }
            `}</style>
        </div>
    );
}
